import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  List,
  Typography,
  Tag,
  Spin,
  message,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Empty,
  Space,
  Tooltip,
  Layout,
} from "antd";
import {
  PlusOutlined,
  BellOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  AlertOutlined,
} from "@ant-design/icons";
import { useGlobal } from "../contexts/GlobalContext";
import { isAdminRole } from "../utils/roles";
import request from "../utils/request";
import { getOpenId } from "../utils/auth";
import {
  isNoticeReadLocally,
  markNoticeReadLocally,
} from "../utils/noticeRead";
import { useLocation } from "react-router-dom";
import MarkdownRenderer from "../components/markdown/MarkdownRenderer";
import styled from "styled-components";
import "remixicon/fonts/remixicon.css";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Content, Sider } = Layout;

// 样式组件定义
const PageContainer = styled.div`
  background: #f0f2f5;
  min-height: 100%;
  border-radius: 8px;
  padding: 16px;
`;

const StyledLayout = styled(Layout)`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: 600px;
  overflow: hidden;

  .ant-layout-header {
    background: white;
    height: 56px;
    line-height: 56px;
    padding: 0 24px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ant-layout-sider {
    background: white;
    border-right: 1px solid #f0f0f0;
  }

  .ant-layout-content {
    background: white;
    padding: 24px;
  }
`;

const NoticeListItem = styled(List.Item)<{ selected: boolean }>`
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin: 8px 0;
  border-radius: 8px;
  padding: 12px 24px;
  background-color: ${(props) => (props.selected ? "#f0f7ff" : "#fff")};

  &:hover {
    background-color: ${(props) => (props.selected ? "#f0f7ff" : "#f5f8ff")};
  }

  .ant-list-item-meta {
    padding: 0 8px;
  }

  .ant-list-item-meta-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px !important;
  }
`;

const NoticeContent = styled.div`
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 24px;

  pre {
    border-radius: 8px !important;
    margin: 16px 0 !important;
  }
`;

const NoticeHeader = styled.div`
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 16px;
`;

// 接口定义
interface NoticeItem {
  id: number;
  title: string;
  content: string;
  created_at: string;
  updated_at?: string;
  level: number;
  product: string;
  hidden: number;
  is_read: boolean;
}

interface NoticeFormData {
  title: string;
  content: string;
  level: number;
  hidden: number;
}

const Notice: React.FC = () => {
  const { API_BASE_URL, token, user } = useGlobal();
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [selectedNotice, setSelectedNotice] = useState<NoticeItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const watchedContent = Form.useWatch("content", form) || "";
  const openId = getOpenId(token);

  // 获取URL查询参数
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const noticeId = queryParams.get("id");

  const fetchNotices = useCallback(
    async (page = 1, pageSize = 10) => {
      setLoading(true);
      try {
        const response = await request.get(`${API_BASE_URL}/api/v3/notices`, {
          params: {
            page,
            page_size: pageSize,
            hidden: isAdminRole(user?.apiInfo?.role) ? -1 : 0,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.code === 200) {
          const noticeList = (response.data.data.list || []).map(
            (item: NoticeItem) => ({
              ...item,
              is_read: item.is_read || isNoticeReadLocally(openId, item),
            })
          );
          setNotices(noticeList);
          setPagination({
            current: page,
            pageSize: pageSize,
            total: response.data.data.total,
          });

          // 如果URL中有ID参数，选中对应的公告
          if (noticeId) {
            const targetNotice = noticeList.find(
              (notice: NoticeItem) => notice.id === parseInt(noticeId)
            );
            if (targetNotice) {
              setSelectedNotice(targetNotice);
            } else if (noticeList.length > 0 && page === 1) {
              // 如果找不到指定ID的公告，且在第一页，默认选中第一条
              setSelectedNotice(noticeList[0]);
            }
          } else if (noticeList.length > 0 && page === 1) {
            // 如果没有ID参数，且在第一页，默认选中第一条
            setSelectedNotice(noticeList[0]);
          }
        } else {
          message.error(response.data.msg);
        }
      } catch (error: any) {
        if (error.response?.data?.msg) {
          message.error(error.response.data.msg);
        } else {
          message.error("获取公告列表失败");
        }
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, token, user?.apiInfo?.role, noticeId, openId]
  );

  const markNoticeRead = useCallback(
    (notice: NoticeItem) => {
      if (!notice || notice.is_read) return;
      markNoticeReadLocally(openId, notice);
      setNotices((prev) =>
        prev.map((item) =>
          item.id === notice.id ? { ...item, is_read: true } : item
        )
      );
      setSelectedNotice((prev) =>
        prev?.id === notice.id ? { ...prev, is_read: true } : prev
      );
      request
        .post(`${API_BASE_URL}/api/v3/notices/${notice.id}/read`)
        .catch((error) => {
          console.error("标记公告已读失败", error);
        });
    },
    [API_BASE_URL, openId]
  );

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  // 处理 URL 参数变化
  useEffect(() => {
    if (noticeId && notices.length > 0) {
      const id = parseInt(noticeId);
      const targetNotice = notices.find(
        (notice: NoticeItem) => notice.id === id
      );
      if (targetNotice) {
        setSelectedNotice(targetNotice);
      }
    }
  }, [noticeId, notices]);

  useEffect(() => {
    if (selectedNotice) {
      markNoticeRead(selectedNotice);
    }
  }, [selectedNotice, markNoticeRead]);

  // 处理新建公告
  const handleAddNotice = async (values: NoticeFormData) => {
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/notice/add`,
        {
          ...values,
          hidden: values.hidden || 0,
          product: "A", // 默认设置为"全部"
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("公告创建成功");
        setIsModalVisible(false);
        form.resetFields();
        fetchNotices(); // 刷新公告列表
      } else {
        message.error(response.data.msg || "创建失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "创建失败");
    }
  };

  const handleUpdateNotice = async (values: NoticeFormData) => {
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/notice/update`,
        {
          ...values,
          id: selectedNotice?.id,
          product: "A",
          hidden: values.hidden || 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("公告更新成功");
        setIsModalVisible(false);
        form.resetFields();
        fetchNotices();
      } else {
        message.error(response.data.msg || "更新失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "更新失败");
    }
  };

  const handleDeleteNotice = async () => {
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/notice/delete`,
        {
          id: selectedNotice?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("公告删除成功");
        setSelectedNotice(null);
        fetchNotices();
      } else {
        message.error(response.data.msg || "删除失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "删除失败");
    }
  };

  // 获取公告等级对应的标签和图标
  const getLevelTag = (level: number) => {
    switch (level) {
      case 1:
        return (
          <Tag color="red" icon={<AlertOutlined />}>
            重要
          </Tag>
        );
      case 2:
        return (
          <Tag color="orange" icon={<ExclamationCircleOutlined />}>
            紧急
          </Tag>
        );
      default:
        return (
          <Tag color="blue" icon={<InfoCircleOutlined />}>
            普通
          </Tag>
        );
    }
  };

  // 格式化日期显示
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 获取公告内容的摘要
  const getContentSummary = (content: string, maxLength: number = 50) => {
    const plainText = content
      .replace(/\\n/g, " ")
      .replace(/\\t/g, " ")
      .replace(/[#*`]/g, "");
    return plainText.length > maxLength
      ? plainText.substring(0, maxLength) + "..."
      : plainText;
  };

  return (
    <PageContainer>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <StyledLayout>
            <Layout.Header>
              <div style={{ display: "flex", alignItems: "center" }}>
                <i
                  className="ri-notification-4-line"
                  style={{
                    fontSize: "20px",
                    color: "#1890ff",
                    marginRight: "8px",
                  }}
                />
                <span style={{ fontSize: "16px", fontWeight: 500 }}>
                  系统公告
                </span>
              </div>
              {isAdminRole(user?.apiInfo?.role) && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedNotice(null);
                    form.resetFields();
                    setIsModalVisible(true);
                  }}
                >
                  新建公告
                </Button>
              )}
            </Layout.Header>
            <Layout>
              <Sider width={320} style={{ background: "#fff" }}>
                <div style={{ padding: "8px" }}>
                  {loading ? (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                      <Spin size="large" />
                    </div>
                  ) : notices.length > 0 ? (
                    <List
                      dataSource={notices}
                      pagination={{
                        ...pagination,
                        onChange: (page, pageSize) =>
                          fetchNotices(page, pageSize),
                        size: "small",
                        hideOnSinglePage: false,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`,
                        showQuickJumper: true,
                        pageSizeOptions: ["10", "20", "30", "50"],
                        style: {
                          marginTop: 16,
                          padding: "0 8px",
                          textAlign: "right",
                        },
                      }}
                      renderItem={(item) => (
                        <NoticeListItem
                          selected={selectedNotice?.id === item.id}
                          onClick={() => setSelectedNotice(item)}
                        >
                          <List.Item.Meta
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  {getLevelTag(item.level)}
                                  {!item.is_read && <Tag color="blue">未读</Tag>}
                                </div>
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: "12px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {formatDate(item.created_at)}
                                </Text>
                              </div>
                            }
                            description={
                              <>
                                <div
                                  style={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    marginBottom: "8px",
                                    color: "#000000",
                                  }}
                                >
                                  {item.title}
                                </div>
                                <div
                                  style={{
                                    color: "#595959",
                                    fontSize: "13px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {getContentSummary(item.content)}
                                </div>
                              </>
                            }
                          />
                        </NoticeListItem>
                      )}
                    />
                  ) : (
                    <Empty
                      description="暂无公告"
                      style={{ padding: "40px 0" }}
                    />
                  )}
                </div>
              </Sider>
              <Content>
                {loading ? (
                  <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin size="large" />
                  </div>
                ) : selectedNotice ? (
                  <>
                    <NoticeHeader>
                      <Title level={3} style={{ margin: 0, marginBottom: 16 }}>
                        {selectedNotice.title}
                      </Title>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Space size={16}>
                          {getLevelTag(selectedNotice.level)}
                          <Text type="secondary">
                            发布时间: {formatDate(selectedNotice.created_at)}
                          </Text>
                        </Space>

                        {isAdminRole(user?.apiInfo?.role) && (
                          <Space>
                            <Tooltip title="编辑公告">
                              <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => {
                                  form.setFieldsValue({
                                    title: selectedNotice.title,
                                    content: selectedNotice.content,
                                    level: selectedNotice.level,
                                    hidden: selectedNotice.hidden,
                                  });
                                  setIsModalVisible(true);
                                }}
                              >
                                编辑
                              </Button>
                            </Tooltip>
                            <Tooltip title="删除公告">
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  Modal.confirm({
                                    title: "确认删除",
                                    content:
                                      "确定要删除这条公告吗？删除后将无法恢复。",
                                    okText: "确认删除",
                                    cancelText: "取消",
                                    onOk: handleDeleteNotice,
                                    okButtonProps: { danger: true },
                                  });
                                }}
                              >
                                删除
                              </Button>
                            </Tooltip>
                          </Space>
                        )}
                      </div>
                    </NoticeHeader>

                    <NoticeContent className="markdown-content">
                      <MarkdownRenderer content={selectedNotice.content} />
                    </NoticeContent>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "80px 0",
                      color: "#999",
                    }}
                  >
                    <Empty
                      description="请选择要查看的公告"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                )}
              </Content>
            </Layout>
          </StyledLayout>
        </Col>
      </Row>

      {/* 新建公告弹窗 */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <BellOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            {selectedNotice ? "编辑公告" : "新建公告"}
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          if (!selectedNotice) {
            setSelectedNotice(null);
          }
        }}
        onOk={() => form.submit()}
        width={960}
        okText={selectedNotice ? "更新" : "发布"}
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={selectedNotice ? handleUpdateNotice : handleAddNotice}
          initialValues={
            selectedNotice
              ? {
                  title: selectedNotice.title,
                  content: selectedNotice.content,
                  level: selectedNotice.level,
                  hidden: selectedNotice.hidden,
                }
              : {
                  level: 0,
                  hidden: 0,
                }
          }
        >
          <Form.Item
            name="title"
            label="公告标题"
            rules={[{ required: true, message: "请输入公告标题" }]}
          >
            <Input placeholder="请输入公告标题" size="large" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="content"
                label="公告内容"
                rules={[{ required: true, message: "请输入公告内容" }]}
              >
                <TextArea
                  rows={14}
                  placeholder="请输入公告内容（支持 Markdown 格式）"
                  style={{ fontSize: "14px", resize: "vertical" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>实时预览</div>
              <div
                style={{
                  minHeight: 312,
                  maxHeight: 420,
                  overflow: "auto",
                  padding: 16,
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  background: "#fff",
                }}
              >
                {watchedContent.trim() ? (
                  <MarkdownRenderer content={watchedContent} />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无预览"
                  />
                )}
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="level" label="通知等级">
                <Select size="large">
                  <Select.Option value={0}>
                    <Space>
                      <InfoCircleOutlined style={{ color: "#1890ff" }} />
                      普通
                    </Space>
                  </Select.Option>
                  <Select.Option value={1}>
                    <Space>
                      <AlertOutlined style={{ color: "#ff4d4f" }} />
                      重要
                    </Space>
                  </Select.Option>
                  <Select.Option value={2}>
                    <Space>
                      <ExclamationCircleOutlined style={{ color: "#fa8c16" }} />
                      紧急
                    </Space>
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="hidden" label="是否隐藏">
                <Select size="large">
                  <Select.Option value={0}>显示</Select.Option>
                  <Select.Option value={1}>隐藏</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default Notice;
